// Helpers for Assessment.jsx — extracted so the submit success/failure
// branching can be unit-tested without mounting the full (heavy) assessment
// form component, matching the house pattern of paymentSuccessSetup.helpers.js.
//
// The public assessment funnel (pre-auth, often paid ad traffic) posts to
// /api/simpleAssessment. Two distinct failures must BOTH surface a visible
// error to the lead instead of silently dropping them:
//   (a) processAssessment throws — the api-client rejects on network errors
//       AND on any non-2xx HTTP response (it turns !response.ok into a thrown
//       Error), so both collapse into the same rejection here.
//   (b) processAssessment resolves but returns no publicToken — the backend
//       answered without a usable result, so there is nothing to route to.
//
// Both cases return `{ ok: false }`; the caller keeps the lead's answers
// intact, re-enables the submit control, and renders the inline error.

/**
 * Run an assessment submission and classify the outcome.
 *
 * @param {object} args
 * @param {(assessmentData: object) => Promise<object>} args.processAssessmentFn
 *   The submit transport (normally the `processAssessment` API wrapper).
 * @param {object} args.assessmentData  Payload to submit.
 * @param {string} [args.requestId] Stable request identity retained across a UI retry.
 * @returns {Promise<{ ok: true, publicToken: string, response: object }
 *                   | { ok: false, response?: object, error?: unknown }>}
 *   `ok: true` only when the backend returned a usable `publicToken`.
 *   Never throws — every failure mode is normalized into `{ ok: false }`.
 */
export async function runAssessmentSubmit({ processAssessmentFn, assessmentData, requestId }) {
  let response;
  try {
    response = requestId
      ? await processAssessmentFn(assessmentData, { requestId })
      : await processAssessmentFn(assessmentData);
  } catch (error) {
    // Covers thrown fetch/network errors AND non-2xx HTTP responses (the
    // api-client rethrows those). The lead must see a retry, not a void.
    return { ok: false, error };
  }

  if (response && response.publicToken) {
    return { ok: true, publicToken: response.publicToken, response };
  }

  // Resolved, but with no token to continue on — treat as a failure so the
  // lead is not stranded on a spinner that silently disappears.
  return { ok: false, response };
}
